import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import { Map, InfoBoxMarker, MapMarker } from 'components/common/GoogleMaps'
import Button from 'components/common/Button/Button'
import Image from 'components/common/Image'
import Icon from 'components/common/Icon/Icon'
import Text from 'components/common/Text/Text'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import { SearchWrapper } from 'components/StoreLocatorPage/components/SharedStyledComponents'
import SavedLocationSearchForm from 'components/common/SavedLocationSearchForm'
import SavedLocationOverview from './SelectedLocationOverview'

const SavedLocation = ({
  isMyAccount,
  fullscreenControl = true,
  findStoreHandleSubmit,
  data: {
    mapCenter,
    address = {},
    owner: {
      ownerImage,
      ownerName,
      firstName,
      latName: lastName,
      title
    } = {},
    ...shopData
  },
  handleCloseClick,
  threeStepper,
  fiveStepper,
  ...props
}) =>
  <Wrapper>
    <SavedLocationOverviewContainer data-at-savedlocation isStepper={ threeStepper || fiveStepper }>
      {
        threeStepper &&
        <Media.Mobile>
          <CloseStoreDetailsButton aria-label="Hide Store Details" link { ...getOnClickAndOnKeyPressDownEvents(handleCloseClick) }>
            <Icon alt="" asset="cross" />
          </CloseStoreDetailsButton>
        </Media.Mobile>
      }
      <SavedLocationOverview
        { ...props }
        isMyAccount={ isMyAccount }
        data={{ ...shopData, address }}
        threeStepper={ threeStepper } />
    </SavedLocationOverviewContainer>

    <Media.Desktop>
      <MapContainer isStepper={ threeStepper || fiveStepper }>
        {
          mapCenter &&
          <Map
            center={ mapCenter }
            fullscreenControl={ fullscreenControl }
            streetViewControl
            zoomControl
            zoom={ 13 }>
            {
              !isMyAccount ?
                <MapMarker position={ mapCenter } /> :
                <InfoBoxMarker position={ mapCenter } >
                  <StoreOwnerBox >
                    <OwnerImageWrapper>
                      { ownerImage ?
                        <Image src={ ownerImage } alt={ ownerName } width={ 70 } height={ 70 } /> :
                        <Icon asset="big-o-logo" width="70px" height="70px" /> }
                    </OwnerImageWrapper>
                    <InfoWrapper>
                      { (firstName || lastName) && <Text bold block size={ 12 } >{firstName} {lastName}</Text> }
                      { title && <Text block size={ 12 } >{ title }<br /></Text> }
                      <Text block lineheight={ '12px' } size={ 12 } >
                        { address.address1 } { address.address2 }<br />{ address.city }, { address.state }
                      </Text>
                    </InfoWrapper>
                  </StoreOwnerBox>
                </InfoBoxMarker>
            }
          </Map>
        }
        {
          threeStepper ?
            <CloseStoreDetailsButton aria-label="Hide Store Details" link { ...getOnClickAndOnKeyPressDownEvents(handleCloseClick) }>
              <Icon alt="" asset="cross" />
            </CloseStoreDetailsButton> :
            <SearchWrapper fullscreenControl={ fullscreenControl }>
              <SavedLocationSearchForm { ...props } onSubmit={ findStoreHandleSubmit } />
            </SearchWrapper>
        }
      </MapContainer>
    </Media.Desktop>
    {
      !isMyAccount &&
      <Media.Mobile>
        <Separator />
        <FormMobileWrapper>
          <FormMobileText data-at-appointment-newlocation-message>
            Or Find a New Location
          </FormMobileText>
          <SavedLocationSearchForm { ...props } onSubmit={ findStoreHandleSubmit } />
        </FormMobileWrapper>
      </Media.Mobile>
    }
  </Wrapper>

SavedLocation.propTypes = {
  data: PropTypes.shape({
    mapCenter: PropTypes.shape(),
    address: PropTypes.shape(),
    owner: PropTypes.shape({
      ownerImage: PropTypes.string,
      ownerName: PropTypes.string,
      firstName: PropTypes.string,
      latName: PropTypes.string,
      title: PropTypes.string
    })
  }),
  findStoreHandleSubmit: PropTypes.func,
  fiveStepper: PropTypes.bool,
  fullscreenControl: PropTypes.bool,
  handleCloseClick: PropTypes.func,
  isMyAccount: PropTypes.bool,
  ownerName: PropTypes.string,
  threeStepper: PropTypes.bool
}

export default SavedLocation

const Wrapper = styled.div`
  text-align: left;
  ${Mixin.clearFix()}
`

const SavedLocationOverviewContainer = styled.div`
  ${Media.desktop`
    position: relative;
    float: left;
    width: 312px;
    border: 1px solid ${Theme.colors.light_grey};
    height: ${props => props.isStepper ? '290px' : '398px'};
  `}
  ${Media.mobile`
    border: 2px solid ${Theme.colors.dark_grey};
    box-shadow: 0 1px 4px ${Theme.colors.transparent_overlay_shadow};
  `}
  ${Mixin.responsive('padding', '15px 15px 0', '15px 10px 15px 20px')};
`

const MapContainer = styled.div`
  position: relative;
  float: right;
  width: calc(100% - 312px);
  height: ${props => props.isStepper ? '290px' : '398px'};
`

const FormMobileWrapper = styled.div`
  position: relative;
  margin-top: 30px;
  padding-bottom: 60px;
`

const StoreOwnerBox = styled.div`
  &::after {
    content: '';
    top: 100%;
    position: absolute;
    width: 0;
    height: 0;
    margin-left: -0.5em;
    bottom: -2em;
    left: 8%;
    box-sizing: border-box;
    border: 10px solid black;
    border-color: transparent transparent #fff #fff;
    transform-origin: 0 0;
    transform: rotate(-45deg);
    box-shadow: -3px 3px 3px 0 rgba(0, 0, 0, 0.3);
  }
`

const OwnerImageWrapper = styled.div`
  position: relative;
  float: left;
  width: 70px;
  height: 70px;
`

const InfoWrapper = styled.div`
  position: relative;
  float: left;
  padding-left: 15px;
  max-width: 170px;
  color: ${Theme.colors.black};
`

const FormMobileText = styled.div`
  position: absolute;
  top: -40px;
  left: 50%;
  margin-left: -89px;
  padding: 0 10px;
  background: ${Theme.colors.white};
  ${Mixin.font.GothamBold(14)}
`

const Separator = styled.div`
  border-top: 1px solid ${Theme.colors.light_grey};
  width: 80%;
  margin: 30px auto 0;
`

const CloseStoreDetailsButton = styled(Button)`
  && {
    position: absolute;
    right: 15px;
    top: 12px;
    background-color: ${Theme.colors.white};
    border: 1px solid ${Theme.colors.light_grey};
    ${Media.mobile`
      border: 0;
      top: 5px;
      z-index: 1;
    `}
  }
`
